import { ApiError, AppError, Language } from '@/types/types';
import { Locale, formatDistance } from 'date-fns';
import { isAxiosError } from 'axios';
import { DateValue } from '@mantine/dates';
import { ErrorCode } from '@/configs/errorCode';
import { CreatorState } from '@/types/creator';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { CreatorLandscapeModule } from '@/api/CreatorLandscapeModuleApi';
import { SerializedRenderer } from '@/renderer/types';
import { ProductModule } from '@/renderer/objects/ProductModule';
import { ModulesGrid } from '@/renderer/utils/ModulesGrid';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';

export function formatDateForInput(date: moment.MomentInput, format?: string) {
	return moment(date, format).format('YYYY-MM-DD');
}

export function formatDateForPresentation(date: moment.MomentInput) {
	if (!date) return '-';
	return moment(date).format('DD.MM.YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput | string = new Date()
) {
	return moment(date).format('HH:mm');
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

const PHONE_REGEX =
	/^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/;

/**
 *
 * @param phoneNumber validate international
 * @returns
 */
export function validatePhoneNumber(phoneNumber: string) {
	return phoneNumber.length === 9 && PHONE_REGEX.test(phoneNumber);
}

export function sortByDate(
	dateA: string | Date,
	dateB: string | Date,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function getContent(conent: Record<string, string>, slug: string) {
	return conent[slug];
}

export function formatDateFromNow(date: Date, locale: Locale) {
	return formatDistance(date, new Date(), {
		locale,
		includeSeconds: true,
		addSuffix: true,
	});
}

export const formatPrice = (value: number, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('pl', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export const isApiError = (error: unknown): error is ApiError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as ApiError).message
	);
};

export const isAppError = (error: unknown): error is AppError => {
	return !!(error as AppError).status && !!(error as AppError).stack;
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatDateForApi(date: DateValue) {
	if (!date) return null;

	return moment(date).format('YYYY-MM-DD');
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function natSort(nameA = '', nameB = '', order: 'asc' | 'desc' = 'asc') {
	let a: string | number = Number(nameA),
		b: string | number = Number(nameB);

	if (isNaN(a) || isNaN(b)) {
		return order === 'asc'
			? nameA.localeCompare(nameB)
			: nameB.localeCompare(nameA);
	}

	return order === 'asc' ? a - b : b - a;
}

export function inputWidthValue(value: string) {
	return `${value.length + 3}ch`;
}

export function roundToHalf(num: number) {
	return Math.round(num * 2) / 2;
}

export function isCreatorActionsEnabled({ options }: CreatorState) {
	return options.isPresetMode || !!options.selectedShape;
}

export function generateProjectPayload(
	creatorState: CreatorState,
	rendererState: SerializedRenderer,
	local = false
) {
	return {
		name: creatorState.name,
		creatorProductModules: creatorState.creatorProductModules.map(
			(creaProdMod) =>
				({
					refId: creaProdMod.refId,
					productModule: local
						? creaProdMod.productModule
						: ({ id: creaProdMod.productModule.id } as ProductModule),
					creatorOptions: creaProdMod.creatorOptions,
				} as CreatorProductModule)
		),
		creatorLandscapeModules: creatorState.creatorLandscapeModules.map(
			(creaLandMod) =>
				({
					refId: creaLandMod.refId,
					landscapeModule: local
						? creaLandMod.landscapeModule
						: ({
								id: creaLandMod.landscapeModule.id,
						  } as CreatorLandscapeModule),
				} as unknown as CreatorLandscapeModule)
		),
		contact: creatorState.contact,
		options: {
			state: {
				renderer: creatorState.renderer,
				options: creatorState.options,
			},
			renderer: rendererState,
		},
	};
}

export function saveProjectToLocal(
	state: CreatorState,
	rendererState: SerializedRenderer
) {
	localStorage.setItem(
		APP_CONFIG.AUTO_SAVE.KEY,
		JSON.stringify(generateProjectPayload(state, rendererState, true))
	);
}

/**
 * @param {number} roomHeight - height in METERS.
 */
export function computeLevelsQuantity(roomHeight: number) {
	return Math.floor(roomHeight / ModulesGrid.BLOCK_HEIGHT);
}
